import { VictoryLabel, VictoryScatter, VictoryTooltip } from 'victory';
import { axisFontStyle, fontStyle, toolTipProps } from '../cbsGraph.config';
import { WebAPICBSPeriodType } from '../../../globals/webApiTypes/elements/WebAPICBS';
import { splitString } from '../helpers/helpers';
import {
	monthShortFormatter,
	yearNumericFormatter,
} from '@dop/shared/helpers/dateTimeFormatters';
import { formatNumber } from '@dop/shared/helpers/string';
import { getLineGraphProps } from './helpers/getLineGraphProps';
import { colors } from '@/uiPrimitives/color/colors.css';
import { SingleDimensionInfo } from './lineGraph.types';

const chartTooltipLineLength = 15;

const formatTooltipUnit = (unit: string) => (unit != null ? ` (${unit})` : '');

const formatDate = (date: Date, periodType: WebAPICBSPeriodType) => {
	const year = yearNumericFormatter.format(date);

	switch (periodType) {
		case 'Maanden': {
			const month = monthShortFormatter.format(date);
			return `${month}. ${year}`;
		}
		case 'Kwartalen': {
			const quarter = Math.floor(date.getMonth() / 3) + 1;
			return `${year} Q${quarter}`;
		}
		case 'Jaar':
			return year;
	}
};

export const Tooltips = ({
	periodType,
	unit,
	decimals,
	globalMiddle = 0,
	filteredDimensionData,
	dimensionLabel,
	chartColor: { color, colorDarker, colorId, symbol },
}: { periodType: WebAPICBSPeriodType } & Pick<
	ReturnType<typeof getLineGraphProps>,
	'unit' | 'decimals' | 'globalMiddle'
> &
	SingleDimensionInfo) => {
	const dimensionLabelArray = splitString(
		dimensionLabel,
		chartTooltipLineLength,
	);
	const formattedUnit = formatTooltipUnit(unit);
	const dimensionLabelLineCount = dimensionLabelArray.length;

	const labelMap = {
		value: {
			fn: ({ y }) =>
				formatNumber({
					decimals,
					number: y,
				}),
			style: {
				fontSize: axisFontStyle.fontSize,
				fill: color,
			},
		},
		unit: {
			fn: () => formattedUnit,
			style: {
				fill: color,
			},
		},
		newLine: {
			fn: () => ' ',
			style: { fontSize: 10 },
		},
	} as const satisfies Record<
		string,
		{
			fn: (datum: { x: Date; y: number }) => string;
			style: Record<string, string | number>;
		}
	>;

	return (
		<VictoryScatter
			name={`tooltipScatter-${colorId}`}
			key={`tooltipScatter-${colorId}`}
			data={filteredDimensionData}
			style={{
				data: {
					strokeWidth: 0,
					stroke: 'none',
					fill: 'none',
				},
			}}
			symbol={symbol}
			labels={({ datum: { x, y } }) => {
				if (y == null) return '';

				return [
					...Object.values(labelMap).map(({ fn }) => fn({ x, y })),
					...dimensionLabelArray,
					formatDate(x, periodType),
				];
			}}
			labelComponent={
				<VictoryTooltip
					{...toolTipProps}
					orientation={({ datum: { y } }) =>
						y < globalMiddle ? 'top' : 'bottom'
					}
					flyoutStyle={{
						strokeWidth: 2,
						stroke: colorDarker,
						fill: 'white',
					}}
					labelComponent={
						<VictoryLabel
							// Set an array of styles, for each separate line of text in the label
							style={[
								...Object.values(labelMap).map(({ style }) => ({
									...fontStyle,
									...style,
								})),
								...[...Array(dimensionLabelLineCount)].map(() => ({
									...fontStyle,
									fill: colors.text.positive.muted,
								})),
								{
									...fontStyle,
									fill: colors.text.positive.muted,
									fontStyle: 'italic',
								},
							]}
						/>
					}
				/>
			}
		/>
	);
};
